import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import '../style.scss';
import { Select } from 'antd';
import Checkbox from 'antd/lib/checkbox';
import {
  IDownloadCenterObject,
  IVersionItem,
} from '../../../model/api-modules/download-center';
import {
  DownloadEngine,
  DownloadObjectType,
  DownloadPlatformType,
  IAPIGameItem,
} from '../../../model/api-modules';
import { getDownloadCenterVersion } from '../../../api/download';
import classNames from 'classnames';
import getFormatMessage from '../../../locales/get-format-message';
import downloadWhitelist from '../../../../src/assets/svg/download-whitelist.svg';
// import downloadVersionSwitch from '../../../../src/assets/svg/download-version-switch.svg';
import SwitchIcon from '../../../assets/svg/switch-icon.svg';
import SwitchIconHover from '../../../assets/svg/switch-icon-hover.svg';
import downloadLatest from '../../../../src/assets/svg/download-latest.svg';
import cppIcon from '../../../../src/assets/svg/cpp-icon.svg';
import winWsvcIcon from '../../../../src/assets/svg/win-msvc-icon.svg';
import zipIcon from '../../../../src/assets/svg/zip-icon.svg';
import windowsIcon from '../../../../src/assets/svg/windows-icon.svg';
import uePluginIcon from '../../../../src/assets/svg/ue-plugin-icon.svg';
import StudioSelectIcon from '../../../assets/svg/studio-select-icon.svg';
import { GameAvatar } from '../../game-content/common/game-avatar';
import { getConfig } from '../../../config';
import { getCurrentLangText } from '../../../lib';
import { CustomPopover } from '../../../components/custom-popover';

interface IOption {
  readonly label: string;
  readonly value: string | number;
}

interface IProps {
  readonly objectItem: IDownloadCenterObject;
  readonly engineOptions: IOption[];
  readonly engineData: any;
  readonly versionOptions: IOption[];
  readonly pssdkVersionOptions: IOption[];
  readonly gdkVersionOptions: IOption[];
  readonly onEngineDataChange: (
    id: number,
    key: string,
    value: number | string,
    versionItem?: IVersionItem
  ) => void;
  readonly selectSdk: number[];
  readonly setSelectSdk: (value: number[]) => void;
  readonly studioList: IAPIGameItem[];
  readonly engineSelectData: any;
  readonly selectPlatform: DownloadPlatformType[];
  readonly subVersionIDsList: {
    id: number;
    subVersionTags: string[];
  }[];
  readonly setSubVersionIDsList: React.Dispatch<
    React.SetStateAction<
      {
        id: number;
        subVersionTags: string[];
      }[]
    >
  >;
}

export const DownloadSdkItem = forwardRef((props: IProps, ref) => {
  const { subVersionIDsList, setSubVersionIDsList } = props;
  const [isShow, setIsShow] = useState<boolean>(false);
  const [showWhitelist, setShowWhitelist] = useState<boolean>(false);
  const [versionOptions, setVersionOptions] = useState<IOption[]>([]);
  const [versionList, setVersionList] = useState<IVersionItem[]>([]);
  const [subVersionTags, setSubVersionTags] = useState<string[]>([]);

  useImperativeHandle(ref, () => ({
    checkItem: (
      id: number,
      isNotChangeSelect?: boolean,
      newEngineSelectData?: any
    ) => checkItem(id, isNotChangeSelect, newEngineSelectData),
  }));

  useEffect(() => {
    const existingIndex = subVersionIDsList.findIndex(
      (item) => item.id === props.objectItem.id
    );
    if (existingIndex >= 0) {
      const updatedList = [...subVersionIDsList];
      updatedList[existingIndex] = {
        id: props.objectItem.id,
        subVersionTags,
      };
      setSubVersionIDsList(updatedList);
    } else {
      const newList = [
        ...subVersionIDsList,
        {
          id: props.objectItem.id,
          subVersionTags,
        },
      ];
      setSubVersionIDsList(newList);
    }

    const engineItem = props.engineSelectData.find(
      (item: any) => item.id === props.objectItem.id
    );
    if (
      engineItem?.versionItem?.SubVersions &&
      engineItem.versionItem.SubVersions.length !== 0 &&
      subVersionTags.length === 0
    ) {
      const newSelectSdk = Object.assign([], props.selectSdk);
      if (newSelectSdk.indexOf(props.objectItem.id) !== -1) {
        newSelectSdk.splice(newSelectSdk.indexOf(props.objectItem.id), 1);
      }
      props.setSelectSdk(newSelectSdk);
    }
  }, [subVersionTags, props.engineData]);

  let avatarColor = '';
  switch (props.objectItem.Type) {
    case DownloadObjectType.Client:
      avatarColor = '#F7BA1E';
      break;
    case DownloadObjectType.Server:
      avatarColor = '#5764F1';
      break;
    case DownloadObjectType.Sample:
      avatarColor = '#F7BA1E';
      break;
    case DownloadObjectType.Tools:
      avatarColor = '#5764F1';
      break;
  }

  useEffect(() => {
    if (getIsShowWhiteList()) {
      setShowWhitelist(true);
    }
    if (getIsInWhite()) {
      initVersion();
      setIsShow(true);
    }
  }, []);

  useEffect(() => {
    // CLIENT-SIDE SDK中sdk选择了不支持的平台则不显示
    if (!getIsInWhite()) {
      return;
    }

    if (
      props.engineSelectData &&
      props.selectPlatform &&
      props.objectItem.Type === DownloadObjectType.Client
    ) {
      let isSupport = true;
      if (props.selectPlatform.length !== 0) {
        const engineItem = props.engineSelectData.find(
          (item: any) => item.id === props.objectItem.id
        );
        const engineJsonItem = engineItem.versionItem?.EngineJson.find(
          (item: any) => item.type === engineItem.engine
        );
        props.selectPlatform.forEach((item) => {
          if (
            !engineJsonItem?.platform ||
            engineJsonItem?.platform.indexOf(item) === -1
          ) {
            isSupport = false;
          }
        });
      } else {
        isSupport = false;
      }
      setIsShow(isSupport);
    }
  }, [props.engineSelectData, props.selectPlatform]);

  const getIsInWhite = () => {
    let isInWhite = false;
    if (props.objectItem.StudioWhitelistArray.length !== 0) {
      props.objectItem.StudioWhitelistArray.forEach((item) => {
        const studioItem = props.studioList.find(
          (studio) => studio.studioID === item
        );
        if (studioItem) {
          isInWhite = true;
        }
      });
    } else {
      isInWhite = true;
    }
    return isInWhite;
  };

  const getIsShowWhiteList = () => {
    if (props.objectItem.StudioWhitelistArray.length !== 0) {
      return props.objectItem.StudioWhitelistArray.some((item) => {
        const studioItem = props.studioList.find(
          (studio) => studio.studioID === item
        );
        if (studioItem) {
          return true;
        }
        return false;
      });
    }
    return false;
  };

  const initVersion = async () => {
    const res = await getDownloadCenterVersion(props.objectItem.id);
    if (res) {
      const currentRes: IVersionItem[] = Object.assign([], res);
      const newRes: IVersionItem[] = currentRes.map((item) => {
        let newEngineJson = item?.EngineJson || [];
        if (item?.SubVersions && item?.SubVersions.length !== 0) {
          const typeMap: any = {};
          for (const it of item?.SubVersions) {
            const engineJson: any = it.EngineJson[0];
            if (!engineJson || typeof engineJson !== 'object') continue;

            const { type } = engineJson;

            if (!typeMap[type]) {
              typeMap[type] = { ...engineJson };
            } else {
              const existing = typeMap[type];
              for (const key in engineJson) {
                if (key === 'type' || key === 'package') {
                  continue;
                }

                const currentValue = engineJson[key];
                const existingValue = existing[key];

                if (currentValue === undefined || currentValue === null) {
                  continue;
                }

                if (Array.isArray(currentValue)) {
                  if (Array.isArray(existingValue)) {
                    const mergedArray = Array.from(
                      new Set([...existingValue, ...currentValue])
                    );
                    existing[key] = mergedArray;
                  } else {
                    existing[key] = Array.from(
                      new Set([...existingValue, ...currentValue])
                    ).filter(Boolean);
                  }
                } else {
                  if (existingValue === undefined) {
                    existing[key] = currentValue;
                  }
                }
              }
            }
          }
          newEngineJson = Object.keys(typeMap).map((i) => {
            return typeMap[i];
          });
        }
        return {
          ...item,
          EngineJson: newEngineJson,
        };
      });
      setVersionList(newRes);
      const options: IOption[] = [];
      newRes.forEach((item) => {
        options.push({
          label: item.Version,
          value: item.id,
        });
      });
      setVersionOptions(options);
    }
  };

  let engineName = '';
  let systemName = '';
  let engineSrc = '';
  switch (props.objectItem.Type) {
    case DownloadObjectType.Client:
      engineName = getFormatMessage('download_engine');
      engineSrc = cppIcon;
      break;
    case DownloadObjectType.Server:
      engineName = getFormatMessage('supporting_system');
      systemName = 'Win/MSVC';
      engineSrc = winWsvcIcon;
      break;
    case DownloadObjectType.Sample:
      engineName = getFormatMessage('supporting_system');
      systemName = 'Windows';
      engineSrc = windowsIcon;
      break;
    case DownloadObjectType.Tools:
      if (props.objectItem.Name === 'MicroService DevTool') {
        engineName = getFormatMessage('download_microservice_format');
        systemName = 'Node.js';
        engineSrc = zipIcon;
      } else {
        engineName = getFormatMessage('download_format');
        systemName = '.zip';
        engineSrc = zipIcon;
      }
      break;
  }

  const renderEngine = (type: number) => {
    if (type !== DownloadObjectType.Client) {
      return (
        <div
          style={{
            width: '400px',
            background: '#F8F8FB',
            borderRadius: 6,
            height: '32px',
            lineHeight: '32px',
          }}
        >
          <img src={engineSrc} className="download-engine-disabled-icon" />
          <span className="download-engine-disabled-text">{systemName}</span>
        </div>
      );
    }
    return (
      <div>
        {props.engineOptions.length === 1 &&
        props.engineOptions[0].value === DownloadEngine.CPP ? (
          <div
            style={{
              width: '400px',
              background: '#F8F8FB',
              borderRadius: 6,
              height: '32px',
              lineHeight: '32px',
            }}
          >
            <img src={engineSrc} className="download-engine-disabled-icon" />
            <span className="download-engine-disabled-text">
              {getFormatMessage('cpp')}
            </span>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex' }}>
              <div
                className="download-select-engine-box"
                onClick={(event) => event.stopPropagation()}
              >
                <Select
                  style={{ width: '220px', height: '32px' }}
                  onChange={(value) =>
                    props.onEngineDataChange(
                      props.objectItem.id,
                      'engine',
                      value
                    )
                  }
                  options={props.engineOptions}
                  value={props.engineData?.engine || ''}
                />
                {props.engineData?.engine ? (
                  <img
                    src={
                      props.engineData?.engine === DownloadEngine.CPP
                        ? cppIcon
                        : uePluginIcon
                    }
                    className="download-select-engine-icon"
                  />
                ) : null}
              </div>
              {(props.engineData?.engine === DownloadEngine.CPP ||
                props.engineData?.engine === DownloadEngine.UEPlugin ||
                props.engineData?.engine === DownloadEngine.UnityPlugin) && (
                <div
                  className="download-select-version-box"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Select
                    style={{ width: '180px' }}
                    onChange={(value) =>
                      props.onEngineDataChange(
                        props.objectItem.id,
                        'version',
                        value
                      )
                    }
                    options={props.versionOptions}
                    value={props.engineData?.version || ''}
                  />
                </div>
              )}
            </div>
            {(props.engineData?.engine === DownloadEngine.CPP ||
              props.engineData?.engine === DownloadEngine.UEPlugin ||
              props.engineData?.engine === DownloadEngine.UnityPlugin) && (
              <div className="download-two-version">
                {(props.selectPlatform.indexOf(DownloadPlatformType.PS4) !==
                  -1 ||
                  props.selectPlatform.indexOf(DownloadPlatformType.PS5) !==
                    -1) && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: '180px',
                      marginRight: '40px',
                      marginTop: '25px',
                    }}
                  >
                    {getFormatMessage('ps_sdk_version')}
                  </span>
                )}
                {props.selectPlatform.indexOf(DownloadPlatformType.XSX) !==
                  -1 && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: '180px',
                      marginTop: '25px',
                    }}
                  >
                    {getFormatMessage('gdk_version')}
                  </span>
                )}
              </div>
            )}
            {(props.engineData?.engine === DownloadEngine.CPP ||
              props.engineData?.engine === DownloadEngine.UEPlugin ||
              props.engineData?.engine === DownloadEngine.UnityPlugin) && (
              <div style={{ display: 'flex' }}>
                {(props.selectPlatform.indexOf(DownloadPlatformType.PS4) !==
                  -1 ||
                  props.selectPlatform.indexOf(DownloadPlatformType.PS5) !==
                    -1) && (
                  <div onClick={(event) => event.stopPropagation()}>
                    <Select
                      style={{ width: '180px', marginRight: '40px' }}
                      onChange={(value) =>
                        props.onEngineDataChange(
                          props.objectItem.id,
                          'pssdkVersion',
                          value
                        )
                      }
                      options={props.pssdkVersionOptions}
                      value={props.engineData?.pssdkVersion || ''}
                    />
                  </div>
                )}
                {props.selectPlatform.indexOf(DownloadPlatformType.XSX) !==
                  -1 && (
                  <div onClick={(event) => event.stopPropagation()}>
                    <Select
                      style={{ width: '180px' }}
                      onChange={(value) =>
                        props.onEngineDataChange(
                          props.objectItem.id,
                          'gdkVersion',
                          value
                        )
                      }
                      options={props.gdkVersionOptions}
                      value={props.engineData?.gdkVersion || ''}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // const onChecked = (e: CheckboxChangeEvent, id: number) => {
  //   const newSelectSdk = Object.assign([], props.selectSdk);
  //   if (e.target.checked && newSelectSdk.indexOf(id) === -1) {
  //     newSelectSdk.push(id);
  //   } else if (!e.target.checked && newSelectSdk.indexOf(id) !== -1) {
  //     newSelectSdk.splice(newSelectSdk.indexOf(id), 1);
  //   }
  //   props.setSelectSdk(newSelectSdk);
  // };

  const checkItem = (
    id: number,
    isNotChangeSelect?: boolean,
    newEngineSelectData?: any
  ) => {
    let engineItem = props.engineSelectData.find(
      (item: any) => item.id === props.objectItem.id
    );
    // 切换版本时check用当前新的id
    if (isNotChangeSelect && newEngineSelectData) {
      engineItem = newEngineSelectData.find(
        (item: any) => item.id === props.objectItem.id
      );
    }
    const selectAbled: string[] = [];
    engineItem?.versionItem?.SubVersions &&
      engineItem.versionItem.SubVersions.length !== 0 &&
      engineItem.versionItem.SubVersions.forEach((item: any) => {
        const subItem = props.objectItem?.SubObject?.find(
          (it) => it.id === item.ObjectID
        );
        let isDisabled = false;
        const itemEngineJson = item.EngineJson[0];
        if (
          itemEngineJson.type !== engineItem.engine ||
          !itemEngineJson.version.includes(engineItem.version)
        ) {
          isDisabled = true;
        } else if (
          engineItem.pssdkVersion &&
          (props.selectPlatform.includes(DownloadPlatformType.PS4) ||
            props.selectPlatform.includes(DownloadPlatformType.PS5)) &&
          !itemEngineJson.pssdkVersion.includes(engineItem.pssdkVersion)
        ) {
          isDisabled = true;
        } else if (
          engineItem.gdkVersion &&
          props.selectPlatform.includes(DownloadPlatformType.XSX) &&
          !itemEngineJson.gdkVersion.includes(engineItem.gdkVersion)
        ) {
          isDisabled = true;
        }
        props.selectPlatform.forEach((it) => {
          if (!itemEngineJson.platform.includes(it)) {
            isDisabled = true;
          }
        });
        if (!isDisabled && subItem && !!subItem.Tag) {
          selectAbled.push(subItem.Tag);
        }
      });
    const newSelectSdk = Object.assign([], props.selectSdk);
    if (isNotChangeSelect) {
      if (
        engineItem.versionItem?.SubVersions &&
        engineItem.versionItem.SubVersions.length !== 0 &&
        selectAbled.length === 0
      ) {
        setSubVersionTags([]);
        return;
      }
      // isNotChangeSelect为true则表示只是切换下载物版本 而不更新选中状态 前后一致 newSelectSdk.indexOf(id) === -1则为未选中状态切换版本
      if (newSelectSdk.indexOf(id) === -1) {
        setSubVersionTags([]);
      } else {
        setSubVersionTags(selectAbled);
      }
    } else {
      if (newSelectSdk.indexOf(id) === -1) {
        if (
          engineItem.versionItem?.SubVersions &&
          engineItem.versionItem.SubVersions.length !== 0 &&
          selectAbled.length === 0
        ) {
          return;
        }
        newSelectSdk.push(id);
        setSubVersionTags(selectAbled);
      } else {
        newSelectSdk.splice(newSelectSdk.indexOf(id), 1);
        setSubVersionTags([]);
      }
      props.setSelectSdk(newSelectSdk);
    }
  };

  const clickTitle = (event: any, link: string) => {
    event.stopPropagation();
    const currentLink = getCurrentLangText(link || '');
    const url =
      currentLink.includes('http') || currentLink.includes('https')
        ? currentLink
        : `${getConfig().consoleURL}${currentLink}`;
    window.open(url, '_blank');
  };

  let itemSize = 0;
  let itemSizeContent = '0 GB';
  const engineSelectData = props.engineSelectData.find(
    (item: any) => item.id === props.objectItem?.id
  );
  const currentEngineJson = engineSelectData?.versionItem?.EngineJson.find(
    (item: any) => item.type === props.engineData?.engine
  );
  if (currentEngineJson) {
    currentEngineJson.package?.length > 0 &&
      currentEngineJson.package.forEach((item: any) => {
        itemSize += item.size;
      });
    const newItemSize = Number((itemSize / 1024).toFixed(1));
    if (newItemSize < 0.1) {
      itemSizeContent = `${itemSize.toFixed(1)} MB`;
    } else {
      itemSizeContent = `${newItemSize} GB`;
    }
  }

  const switchVersion = (value: number) => {
    const versionItem = versionList.find((item) => item.id === value);
    props.onEngineDataChange(
      props.objectItem.id,
      'versionId',
      value,
      versionItem
    );
    // checkItem(props.objectItem.id, true);
  };

  const changeSubRadio = (tag: string) => {
    if (!tag) return;
    const currentSubVersionIDs = Object.assign([], subVersionTags);
    if (currentSubVersionIDs.includes(tag)) {
      currentSubVersionIDs.splice(currentSubVersionIDs.indexOf(tag), 1);
    } else {
      currentSubVersionIDs.push(tag);
    }
    setSubVersionTags(currentSubVersionIDs);

    const newSelectSdk = Object.assign([], props.selectSdk);
    if (newSelectSdk.indexOf(props.objectItem.id) === -1) {
      newSelectSdk.push(props.objectItem.id);
    }
    props.setSelectSdk(newSelectSdk);
  };

  if (isShow) {
    const engineItem = props.engineSelectData.find(
      (item: any) => item.id === props.objectItem.id
    );
    const versionItem = versionOptions.find(
      (item) => item.value === engineItem.versionId
    );
    const version = versionItem ? versionItem.label : '';
    return (
      <div
        className={classNames(
          'download-sdk-box',
          props.selectSdk.indexOf(props.objectItem.id) !== -1
            ? 'download-sdk-select'
            : ''
        )}
        onClick={() => checkItem(props.objectItem.id)}
      >
        {showWhitelist && (
          <img src={downloadWhitelist} className="download-sdk-white" />
        )}
        <GameAvatar
          width="40px"
          name={props.objectItem.Name}
          fontSize="22px"
          color={avatarColor}
        />
        <div className="download-sdk-content">
          <div className="download-sdk-header-box">
            <div className="download-sdk-title-box">
              <div
                className="download-sdk-title"
                onClick={(event) => clickTitle(event, props.objectItem.DocLink)}
              >
                {props.objectItem.Name}
              </div>
              <div className="download-sdk-version">{version}</div>
              <div className="download-sdk-switch-version">
                <img
                  src={SwitchIcon}
                  className={classNames(
                    'download-sdk-switch-version-icon',
                    'switch-icon'
                  )}
                />
                <img
                  src={SwitchIconHover}
                  className={classNames(
                    'download-sdk-switch-version-icon',
                    'switch-icon-hover'
                  )}
                />
                <div
                  className="download-sdk-switch-version-box"
                  onClick={(event) => event.stopPropagation()}
                >
                  {versionOptions.map((item) => {
                    return (
                      <div
                        onClick={() => switchVersion(Number(item.value))}
                        className={classNames(
                          'download-sdk-switch-version-item',
                          engineItem.versionId === item.value
                            ? 'selected-version'
                            : ''
                        )}
                      >
                        <div className="download-sdk-switch-version-text">
                          {item.label}
                        </div>
                        {engineItem.versionId === item.value && (
                          <img
                            src={StudioSelectIcon}
                            className="download-sdk-switch-version-select-icon"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {props.objectItem.VersionItem?.id === engineItem.versionId && (
                <img src={downloadLatest} className="download-latest" />
              )}
            </div>
            <Checkbox
              // onChange={(e) => onChecked(e, props.objectItem.id)}
              className="download-check-selected"
              checked={props.selectSdk.indexOf(props.objectItem.id) !== -1}
            />
          </div>
          <div className="download-desc-box">
            <span className="download-desc-left">{`${itemSizeContent}`}</span>
            <span>
              {getCurrentLangText(props.objectItem.Description) || ''}
            </span>
          </div>
          <div className="download-engine-name">{engineName}</div>
          {renderEngine(props.objectItem.Type)}
          {engineItem?.versionItem?.SubVersions &&
            engineItem.versionItem.SubVersions.length !== 0 && (
              <div className="download-engine-options">
                <div className="download-engine-options-one">
                  {getFormatMessage('service_download_options')}
                </div>
                <div className="download-engine-options-two">
                  {getFormatMessage('downloaded_sdk_selected_tips')}
                </div>
                {engineItem.versionItem.SubVersions.map(
                  (item: IVersionItem) => {
                    if (props.objectItem.SubObject) {
                      if (item.EngineJson[0].type !== engineItem?.engine) {
                        return null;
                      }
                      const subItem = props.objectItem.SubObject.find(
                        (it) => it.id === item.ObjectID
                      );
                      let isDisabled = false;
                      let notSupportTips = '';
                      let notSupportPlatformTips = '';
                      const itemEngineJson = item.EngineJson[0];
                      if (
                        itemEngineJson.type !== props.engineData.engine ||
                        !itemEngineJson.version.includes(
                          props.engineData.version
                        )
                      ) {
                        isDisabled = true;
                        if (
                          props.engineData.engine === DownloadEngine.UEPlugin
                        ) {
                          notSupportTips += getFormatMessage('ue_plugin');
                        } else if (
                          props.engineData.engine === DownloadEngine.UnityPlugin
                        ) {
                          notSupportTips += getFormatMessage('unity_plugin');
                        }
                        notSupportTips += props.engineData.version;
                      } else if (
                        props.engineData.pssdkVersion &&
                        (props.selectPlatform.includes(
                          DownloadPlatformType.PS4
                        ) ||
                          props.selectPlatform.includes(
                            DownloadPlatformType.PS5
                          )) &&
                        !itemEngineJson.pssdkVersion.includes(
                          props.engineData.pssdkVersion
                        )
                      ) {
                        isDisabled = true;
                        notSupportTips += `${getFormatMessage('ps_sdk')}${
                          props.engineData.pssdkVersion
                        }`;
                      } else if (
                        props.engineData.gdkVersion &&
                        props.selectPlatform.includes(
                          DownloadPlatformType.XSX
                        ) &&
                        !itemEngineJson.gdkVersion.includes(
                          props.engineData.gdkVersion
                        )
                      ) {
                        isDisabled = true;
                        notSupportTips += `${getFormatMessage('gdk')}${
                          props.engineData.gdkVersion
                        }`;
                      }
                      props.selectPlatform.forEach((it) => {
                        if (!itemEngineJson.platform.includes(it)) {
                          isDisabled = true;
                          let name = '';
                          switch (it) {
                            case DownloadPlatformType.PS4:
                              name = 'PS4';
                              break;
                            case DownloadPlatformType.PS5:
                              name = 'PS5';
                              break;
                            case DownloadPlatformType.XSX:
                              name = 'XSX';
                              break;
                            case DownloadPlatformType.Switch:
                              name = 'Switch';
                              break;
                            case DownloadPlatformType.Windows:
                              name = 'Windows';
                              break;
                            case DownloadPlatformType.Mac:
                              name = 'MacOS';
                              break;
                            case DownloadPlatformType.Android:
                              name = 'Android';
                              break;
                            case DownloadPlatformType.IOS:
                              name = 'IOS';
                              break;
                          }
                          notSupportPlatformTips += `${name},`;
                        }
                      });
                      if (
                        isDisabled &&
                        subItem &&
                        !!subItem.Tag &&
                        subVersionTags.includes(subItem.Tag)
                      ) {
                        const currentSubVersionIDs = Object.assign(
                          [],
                          subVersionTags
                        );
                        if (
                          subItem &&
                          !!subItem.Tag &&
                          currentSubVersionIDs.includes(subItem.Tag)
                        )
                          currentSubVersionIDs.splice(
                            currentSubVersionIDs.indexOf(subItem.Tag),
                            1
                          );
                        setSubVersionTags(currentSubVersionIDs);
                      }
                      if (!notSupportTips && notSupportPlatformTips) {
                        notSupportPlatformTips = notSupportPlatformTips.slice(
                          0,
                          -1
                        );
                      }
                      if (subItem) {
                        return (
                          <div
                            onClick={(event) => event.stopPropagation()}
                            className="sub-checkbox"
                          >
                            {isDisabled ? (
                              <CustomPopover
                                content={getFormatMessage('does_not_support', {
                                  params: {
                                    name: subItem.Name,
                                    config: `${notSupportPlatformTips} ${notSupportTips}`,
                                  },
                                })}
                              >
                                <Checkbox
                                  className="download-engine-options-ridio"
                                  disabled
                                  style={{ lineHeight: '28px' }}
                                >
                                  <span className="download-engine-options-ridio-left">
                                    {subItem.Name}
                                  </span>
                                  <span className="download-engine-options-ridio-right">{`(${
                                    getCurrentLangText(subItem.Description) ||
                                    ''
                                  })`}</span>
                                </Checkbox>
                              </CustomPopover>
                            ) : (
                              <Checkbox
                                className={classNames(
                                  'download-engine-options-ridio',
                                  !!subItem.Tag &&
                                    subVersionTags.includes(subItem.Tag)
                                    ? 'download-engine-options-ridio-active'
                                    : ''
                                )}
                                checked={
                                  !!subItem.Tag &&
                                  subVersionTags.includes(subItem.Tag)
                                }
                                onClick={(e) => {
                                  changeSubRadio(subItem.Tag || '');
                                  const input = e.currentTarget;
                                  if (input) input.blur();
                                }}
                              >
                                <span className="download-engine-options-ridio-left">
                                  {subItem.Name}
                                </span>
                                <span className="download-engine-options-ridio-right">{`(${
                                  getCurrentLangText(subItem.Description) || ''
                                })`}</span>
                              </Checkbox>
                            )}
                          </div>
                        );
                      }
                    }
                    return null;
                  }
                )}
              </div>
            )}
        </div>
      </div>
    );
  }
  return null;
});
